import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Image } from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link2,
  Image as ImageIcon,
  Table as TableIcon,
  Code,
  Code2,
  Heading1,
  Heading2,
  Heading3
} from 'lucide-react';
import './TiptapEditor.css';

interface TiptapEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
}

const TiptapEditor: React.FC<TiptapEditorProps> = ({ content, onChange, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: placeholder || 'Digite o conteúdo aqui...',
      }),
      Typography,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      
      Image.configure({
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'editor-table',
        },
      }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('URL');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('URL da imagem');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  return (
    <div className="tiptap-editor-container flex flex-col h-full">
      {/* Toolbar */}
      <div className="tiptap-toolbar">
        <div className="tiptap-toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`tiptap-toolbar-item ${editor.isActive('bold') ? 'is-active' : ''}`}
            title="Negrito"
          >
            <Bold size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`tiptap-toolbar-item ${editor.isActive('italic') ? 'is-active' : ''}`}
            title="Itálico"
          >
            <Italic size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`tiptap-toolbar-item ${editor.isActive('underline') ? 'is-active' : ''}`}
            title="Sublinhado"
          >
            <UnderlineIcon size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`tiptap-toolbar-item ${editor.isActive('strike') ? 'is-active' : ''}`}
            title="Tachado"
          >
            <Strikethrough size={16} />
          </button>
        </div>

        <div className="tiptap-toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`tiptap-toolbar-item ${editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`}
            title="Título 1"
          >
            <Heading1 size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`tiptap-toolbar-item ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
            title="Título 2"
          >
            <Heading2 size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`tiptap-toolbar-item ${editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}`}
            title="Título 3"
          >
            <Heading3 size={16} />
          </button>
        </div>

        <div className="tiptap-toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`tiptap-toolbar-item ${editor.isActive('bulletList') ? 'is-active' : ''}`}
            title="Lista com marcadores"
          >
            <List size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`tiptap-toolbar-item ${editor.isActive('orderedList') ? 'is-active' : ''}`}
            title="Lista numerada"
          >
            <ListOrdered size={16} />
          </button>
        </div>

        <div className="tiptap-toolbar-group">
          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`tiptap-toolbar-item ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`}
            title="Alinhar à esquerda"
          >
            <AlignLeft size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`tiptap-toolbar-item ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`}
            title="Centralizar"
          >
            <AlignCenter size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`tiptap-toolbar-item ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`}
            title="Alinhar à direita"
          >
            <AlignRight size={16} />
          </button>
        </div>

        <div className="tiptap-toolbar-group">
          <button
            onClick={addLink}
            className={`tiptap-toolbar-item ${editor.isActive('link') ? 'is-active' : ''}`}
            title="Adicionar link"
          >
            <Link2 size={16} />
          </button>
          <button
            onClick={addImage}
            className="tiptap-toolbar-item"
            title="Adicionar imagem"
          >
            <ImageIcon size={16} />
          </button>
          <button
            onClick={addTable}
            className="tiptap-toolbar-item"
            title="Adicionar tabela"
          >
            <TableIcon size={16} />
          </button>
        </div>

        <div className="tiptap-toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleCode().run()}
            className={`tiptap-toolbar-item ${editor.isActive('code') ? 'is-active' : ''}`}
            title="Código inline"
          >
            <Code size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={`tiptap-toolbar-item ${editor.isActive('codeBlock') ? 'is-active' : ''}`}
            title="Bloco de código"
          >
            <Code2 size={16} />
          </button>
        </div>
      </div>

      {/* Editor Content */}
      <div className="tiptap-editor-content h-full overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default TiptapEditor; 